/**
 * The Facelift desktop (Electron) tool bridge — the capability contract
 * for the desktop-only `client_file_*` / `client_exec_*` agent tools
 * (see `planning/desktop-agent-tools.md`).
 *
 * The bridge is injected by the `facelift-desktop` preload into every
 * account WebView. Its presence is the **capability gate**: registration
 * of the local tool families keys on {@link getDesktopTools} returning a
 * bridge, never on User-Agent sniffing (`isFaceliftDesktop()` is UI
 * cosmetics — a spoofed UA creates no bridge). Browsers, iPad and Android
 * never see this object.
 *
 * The bridge is deliberately thin: it forwards named operations and their
 * parameters to the Electron main process, which owns execution *and* the
 * permission gate. The renderer can request, never decide — results carry
 * an error and possibly a `denyReason`, never the verdict internals or the
 * policy.
 */
const BRIDGE_KEY = 'vanceDesktopTools';
/**
 * The injected bridge, or `null` when not running inside the Facelift
 * desktop app. Presence = capability; use this as the gate, not the UA.
 */
export function getDesktopTools() {
    if (typeof window === 'undefined')
        return null;
    const bridge = window[BRIDGE_KEY];
    return isBridge(bridge) ? bridge : null;
}
/** Structural guard — an injected bridge must expose all three members. */
function isBridge(value) {
    if (typeof value !== 'object' || value === null)
        return false;
    const candidate = value;
    return (typeof candidate.version === 'string' &&
        typeof candidate.getContext === 'function' &&
        typeof candidate.toolsEnabled === 'object' &&
        candidate.toolsEnabled !== null &&
        typeof candidate.toolsEnabled.get === 'function' &&
        typeof candidate.invoke === 'function' &&
        // packs arrived with the tool-pack stage — an app build without it
        // still counts as a bridge (the face treats it as an empty toolbox)
        (candidate.packs === undefined ||
            (typeof candidate.packs === 'object' &&
                candidate.packs !== null &&
                typeof candidate.packs.list === 'function')));
}
//# sourceMappingURL=desktopTools.js.map